import React, { useState, useEffect } from 'react';
import { apiService } from './services/api'; 
import './style/PageStyle/CategoryManager.css';

// --- MODAL COMPONENT ---
const UserListModal = ({ category, allUsers, onClose, onRemoveUser, onAddUser }) => {
  const [selectedNewUserId, setSelectedNewUserId] = useState('');

  if (!category) return null;

  // 1. Determine currently assigned users
  let currentAssignedUsers = [];
  if (category.assignedUsers && category.assignedUsers.length > 0) {
    currentAssignedUsers = category.assignedUsers;
  } else if (category.user_ids && category.user_ids.length > 0) {
    currentAssignedUsers = category.user_ids.map(id => {
      return allUsers.find(u => u.id === id) || { id, username: `Unknown (ID: ${id})` };
    });
  }

  // 2. Filter users available to be added (must match role, must not already be in list)
  const availableUsers = allUsers.filter(u => {
    // Must match the category's role restriction (if one exists)
    if (category.allowed_role_key && (u.role_key || u.role) !== category.allowed_role_key) {
      return false;
    }
    // Must not already be assigned
    const isAlreadyAssigned = currentAssignedUsers.some(assigned => assigned.id === u.id);
    return !isAlreadyAssigned;
  });

  const handleAddClick = () => {
    if (!selectedNewUserId) return;
    onAddUser(category.id, parseInt(selectedNewUserId, 10));
    setSelectedNewUserId(''); // Reset dropdown
  };

  return (
    <div className="cm-modal-overlay">
      <div className="cm-modal">
        <div className="cm-modal-header">
          <h3>Manage Users: "{category.name}"</h3>
          <button onClick={onClose} className="cm-modal-close">×</button>
        </div>
        
        <div className="cm-modal-body">
          
          {/* ADD USER SECTION */}
          <div className="cm-modal-add-section" style={{ marginBottom: '15px', paddingBottom: '15px', borderBottom: '1px solid #eee' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Add User to Category:</label>
            <div style={{ display: 'flex', gap: '10px' }}>
              <select 
                className="cm-select" 
                value={selectedNewUserId} 
                onChange={(e) => setSelectedNewUserId(e.target.value)}
                style={{ flex: 1 }}
              >
                <option value="">-- Select User to Add --</option>
                {availableUsers.length === 0 ? (
                  <option disabled>No eligible users found</option>
                ) : (
                  availableUsers.map(u => (
                    <option key={u.id} value={u.id}>{u.username}</option>
                  ))
                )}
              </select>
              <button 
                className="cm-btn cm-btn-primary" 
                onClick={handleAddClick}
                disabled={!selectedNewUserId}
              >
                Add
              </button>
            </div>
          </div>

          {/* LIST USERS SECTION */}
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Current Users:</label>
          {currentAssignedUsers.length === 0 ? (
            <p className="cm-text-muted">No users assigned yet.</p>
          ) : (
            <ul className="cm-user-list">
              {currentAssignedUsers.map(user => (
                <li key={user.id} className="cm-user-list-item">
                  <span>{user.username}</span>
                  <button 
                    className="cm-btn-xs cm-btn-danger"
                    onClick={() => onRemoveUser(category.id, user.id)}
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

// --- MAIN COMPONENT ---
const CategoryManager = () => {
  const [categories, setCategories] = useState([]);
  const [roles, setRoles] = useState([]); 
  const [allUsers, setAllUsers] = useState([]); 
  
  // Form State
  const [name, setName] = useState('');
  const [selectedRoleKey, setSelectedRoleKey] = useState(''); 
  const [selectedUserIds, setSelectedUserIds] = useState([]); 
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);

  // Modal State
  const [viewingCategory, setViewingCategory] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setIsLoading(true);
      const [catsData, rolesData, usersData] = await Promise.all([
        apiService.getCategories(),
        apiService.getRoles ? apiService.getRoles() : Promise.resolve([]), 
        apiService.getUsers ? apiService.getUsers() : Promise.resolve([])
      ]);
      
      setCategories(Array.isArray(catsData) ? catsData : []);
      setRoles(Array.isArray(rolesData) ? rolesData : []);
      setAllUsers(Array.isArray(usersData) ? usersData : []);

    } catch (err) {
      console.error("Error loading data:", err);
      alert("Failed to load data. Please check console.");
    } finally {
      setIsLoading(false);
    }
  };

  const getFilteredUsers = () => {
    if (!selectedRoleKey) return [];
    return allUsers.filter(u => (u.role_key || u.role) === selectedRoleKey);
  };

  const resetForm = () => {
    setName('');
    setSelectedRoleKey('');
    setSelectedUserIds([]);
    setIsEditing(false);
    setEditId(null);
  };

  const handleEditClick = (cat) => {
    setIsEditing(true);
    setEditId(cat.id);
    setName(cat.name);
    setSelectedRoleKey(cat.allowed_role_key || '');
    setSelectedUserIds(cat.user_ids || []);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: name,
        allowed_role_key: selectedRoleKey || null,
        user_ids: selectedUserIds 
      };

      if (isEditing) {
        await apiService.updateCategory(editId, payload);
      } else {
        await apiService.createCategory(payload);
      }
      resetForm();
      loadData(); 
    } catch (error) {
      console.error(error);
      alert(`Failed to save. Error: ${error.message || 'Unknown error'}`);
    }
  };

  const handleUserSelect = (e) => {
    const userId = parseInt(e.target.value, 10);
    if (!userId) return;
    if (!selectedUserIds.includes(userId)) {
      setSelectedUserIds(prev => [...prev, userId]);
    }
  };

  const removeUserFromSelection = (userIdToRemove) => {
    setSelectedUserIds(selectedUserIds.filter(id => id !== userIdToRemove));
  };

  // --- NEW: Handle Adding User from Modal ---
  const handleAddUserToCategory = async (catId, userId) => {
    try {
      const cat = categories.find(c => c.id === catId);
      if (!cat) return;

      const currentUserIds = cat.user_ids || [];
      const newUserIds = [...currentUserIds, userId];

      const payload = {
        name: cat.name,
        allowed_role_key: cat.allowed_role_key,
        user_ids: newUserIds
      };

      await apiService.updateCategory(catId, payload);

      // Optimistic Update
      const userObj = allUsers.find(u => u.id === userId);
      
      setCategories(prevCats => prevCats.map(c => {
        if (c.id === catId) {
          return { 
            ...c, 
            user_ids: newUserIds, 
            assignedUsers: [...(c.assignedUsers || []), userObj] 
          };
        }
        return c;
      }));

      if (viewingCategory && viewingCategory.id === catId) {
        setViewingCategory(prev => ({
          ...prev,
          user_ids: newUserIds,
          assignedUsers: [...(prev.assignedUsers || []), userObj]
        }));
      }

    } catch (error) {
      console.error("Failed to add user", error);
      alert("Failed to add user.");
    }
  };

  const handleRemoveUserFromCategory = async (catId, userId) => {
    if(!window.confirm("Remove this user from the category?")) return;

    try {
      await apiService.removeUserFromCategory(catId, userId);
      
      // Update Local State (Optimistic UI Update)
      setCategories(prevCats => prevCats.map(cat => {
        if (cat.id === catId) {
          const newUserIds = cat.user_ids.filter(id => id !== userId);
          const newAssignedUsers = (cat.assignedUsers || []).filter(u => u.id !== userId);
          return { ...cat, user_ids: newUserIds, assignedUsers: newAssignedUsers };
        }
        return cat;
      }));

      // Update Modal State if open
      if (viewingCategory && viewingCategory.id === catId) {
        setViewingCategory(prev => ({
          ...prev,
          user_ids: prev.user_ids.filter(id => id !== userId),
          assignedUsers: (prev.assignedUsers || []).filter(u => u.id !== userId)
        }));
      }
    } catch (error) {
      console.error("Failed to remove user", error);
      alert("Failed to remove user.");
    }
  };

  // --- DELETE HANDLER WITH CHECK ---
  const handleDeleteClick = async (cat) => {
    // 1. Check if files exist
    if (cat.doc_count > 0) {
      alert(`Cannot delete category "${cat.name}" because it contains ${cat.doc_count} file(s). Please move or delete the files first.`);
      return;
    }

    if(window.confirm(`Are you sure you want to delete category "${cat.name}"?`)) {
      try {
        await apiService.deleteCategory(cat.id);
        loadData();
      } catch (error) {
        // Handle backend rejection if frontend check fails
        const msg = error.response?.data?.error || error.message;
        alert(`Failed to delete: ${msg}`);
      }
    }
  };

  if (isLoading) return <div className="cm-loading">Loading...</div>;

  return (
    <div className="cm-container">
      <div className="cm-header">
        <h2>Category Management</h2>
      </div>
      
      <div className="cm-content-wrapper">
        {/* Form Section */}
        <div className="cm-form-section">
          <div className="cm-form-header-row">
            <h3>{isEditing ? 'Edit Category' : 'Add New Category'}</h3>
            {isEditing && <button type="button" className="cm-btn-text" onClick={resetForm}>Cancel</button>}
          </div>
          
          <form onSubmit={handleSubmit} className="cm-form">
            <div className="cm-form-group">
              <label>Category Name</label>
              <input value={name} onChange={e => setName(e.target.value)} className="cm-input" required />
            </div>

            <div className="cm-form-group">
              <label>Restrict to Role</label>
              <select 
                value={selectedRoleKey} 
                onChange={e => { setSelectedRoleKey(e.target.value); setSelectedUserIds([]); }}
                className="cm-select"
              >
                <option value="">-- Select Role --</option>
                {roles.map(r => <option key={r.id} value={r.roleKey}>{r.name}</option>)}
              </select>
            </div>

            <div className="cm-form-group">
              <label>Assign Users ({selectedUserIds.length} selected)</label>
              <select className="cm-select" disabled={!selectedRoleKey} onChange={handleUserSelect} value="">
                <option value="" disabled>Select Users...</option>
                {getFilteredUsers().map(u => (
                  <option key={u.id} value={u.id} disabled={selectedUserIds.includes(u.id)}>
                    {selectedUserIds.includes(u.id) ? '✓ ' : ''} {u.username}
                  </option>
                ))}
              </select>
              <small style={{color: '#666', fontSize: '0.8em'}}>
                Note: If you assign users, only those users (and you, if assigned) will see this category.
              </small>
            </div>

            {selectedUserIds.length > 0 && (
              <div className="cm-tags-container">
                {selectedUserIds.map(id => {
                  const u = allUsers.find(user => user.id === id);
                  return u ? (
                    <span key={id} className="cm-tag" onClick={() => removeUserFromSelection(id)}>
                      {u.username} <span className="cm-tag-close">×</span>
                    </span>
                  ) : null;
                })}
              </div>
            )}

            <button type="submit" className={`cm-btn ${isEditing ? 'cm-btn-warning' : 'cm-btn-primary'}`}>
              {isEditing ? 'Update' : 'Create'}
            </button>
          </form>
        </div>

        {/* List Section */}
        <div className="cm-list-section">
          <h3>Existing Categories</h3>
          <div className="cm-table-container">
            <table className="cm-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Role</th>
                  <th>Files</th>
                  <th>Assigned Users</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {categories.map(cat => (
                  <tr key={cat.id} className={editId === cat.id ? 'cm-row-active' : ''}>
                    <td className="cm-fw-bold">{cat.name}</td>
                    <td>
                      <span className={`cm-badge ${cat.allowed_role_key ? 'cm-badge-role' : 'cm-badge-all'}`}>
                        {cat.allowed_role_key || 'All'}
                      </span>
                    </td>
                    <td>
                      <span className={`cm-badge ${cat.doc_count > 0 ? 'cm-badge-role' : 'cm-badge-all'}`} style={{background: cat.doc_count > 0 ? '#e2e8f0' : '#f1f5f9', color: '#334155'}}>
                        {cat.doc_count || 0}
                      </span>
                    </td>
                    <td>
                      {/* CLICKABLE LINK */}
                      {cat.user_ids && cat.user_ids.length > 0 ? (
                        <button 
                          className="cm-link-btn"
                          onClick={() => setViewingCategory(cat)}
                        >
                          {cat.user_ids.length} User{cat.user_ids.length !== 1 ? 's' : ''}
                        </button>
                      ) : (
                        <span className="cm-text-muted">None (Public)</span>
                      )}
                    </td>
                    <td>
                      <div className="cm-action-buttons">
                        <button className="cm-btn-icon edit" onClick={() => handleEditClick(cat)}>✎</button>
                        <button 
                          className="cm-btn-icon delete" 
                          onClick={() => handleDeleteClick(cat)}
                          style={{ opacity: cat.doc_count > 0 ? 0.5 : 1, cursor: cat.doc_count > 0 ? 'not-allowed' : 'pointer' }}
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* RENDER MODAL */}
      {viewingCategory && (
        <UserListModal 
          category={viewingCategory} 
          allUsers={allUsers} 
          onClose={() => setViewingCategory(null)}
          onRemoveUser={handleRemoveUserFromCategory}
          onAddUser={handleAddUserToCategory} 
        />
      )}
    </div>
  );
};

export default CategoryManager;
